import { useState, useEffect } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  streamingServiceApi,
  EstreamingServiceStatus,
  UpdateStreamingServiceRequest,
  StreamingService,
} from "@/services/streaming-service";
import { Loader2, Check } from "lucide-react";
import { cn } from "@/lib/utils";

interface EditStreamingServiceModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  service: StreamingService | null;
}

// Iconos predefinidos para servicios de streaming
const PREDEFINED_ICONS = [
  { icon: "🎬", label: "Netflix" },
  { icon: "📺", label: "HBO" },
  { icon: "🏰", label: "Disney" },
  { icon: "📦", label: "Prime" },
  { icon: "🎵", label: "Spotify" },
  { icon: "⭐", label: "Paramount" },
  { icon: "🍎", label: "Apple" },
  { icon: "🎮", label: "Gaming" },
  { icon: "📡", label: "TV" },
  { icon: "🎭", label: "Entertainment" },
  { icon: "🎥", label: "Movies" },
  { icon: "📱", label: "Mobile" },
  { icon: "🌐", label: "Web" },
  { icon: "💎", label: "Premium" },
  { icon: "🔥", label: "Hot" },
  { icon: "⚡", label: "Fast" },
];

// Colores predefinidos para servicios de streaming
const PREDEFINED_COLORS = [
  { color: "#E50914", label: "Netflix Red" },
  { color: "#5822B4", label: "HBO Purple" },
  { color: "#113CCF", label: "Disney Blue" },
  { color: "#00A8E1", label: "Prime Blue" },
  { color: "#1DB954", label: "Spotify Green" },
  { color: "#0064FF", label: "Paramount Blue" },
  { color: "#000000", label: "Apple Black" },
  { color: "#FF6B00", label: "Crunchyroll Orange" },
  { color: "#E91E63", label: "Pink" },
  { color: "#9C27B0", label: "Purple" },
  { color: "#673AB7", label: "Deep Purple" },
  { color: "#3F51B5", label: "Indigo" },
  { color: "#2196F3", label: "Blue" },
  { color: "#009688", label: "Teal" },
  { color: "#4CAF50", label: "Green" },
  { color: "#FF5722", label: "Deep Orange" },
];

export function EditStreamingServiceModal({
  open,
  onOpenChange,
  service,
}: EditStreamingServiceModalProps) {
  const queryClient = useQueryClient();

  const [formData, setFormData] = useState({
    name: "",
    icon: "",
    color: "",
    status: EstreamingServiceStatus.ACTIVE,
  });

  // Cargar datos del servicio al abrir el modal
  useEffect(() => {
    if (open && service) {
      setFormData({
        name: service.name || "",
        icon: service.icon || "",
        color: service.color || "",
        status: service.status as EstreamingServiceStatus,
      });
    }
  }, [open, service]);

  // Mutation para actualizar servicio
  const updateMutation = useMutation({
    mutationFn: (data: UpdateStreamingServiceRequest) =>
      streamingServiceApi.update(String(service.id), data),
    onSuccess: () => {
      toast.success("Servicio de streaming actualizado exitosamente");
      queryClient.invalidateQueries({ queryKey: ["streaming-services"] });
      queryClient.invalidateQueries({
        queryKey: ["streaming-service", String(service.id)],
      });
      handleClose();
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  const handleClose = () => {
    onOpenChange(false);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      toast.error("Ingresa el nombre del servicio");
      return;
    }

    if (!formData.icon) {
      toast.error("Selecciona un icono para el servicio");
      return;
    }

    if (!formData.color) {
      toast.error("Selecciona un color para el servicio");
      return;
    }

    updateMutation.mutate({
      name: formData.name,
      icon: formData.icon,
      color: formData.color,
      status: formData.status,
    });
  };

  if (!service) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
      <DialogContent className="sm:max-w-[550px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Editar Servicio de Streaming</DialogTitle>
          <DialogDescription>
            Modifica los detalles del servicio de streaming.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Nombre del servicio */}
          <div className="space-y-2">
            <Label htmlFor="name">Nombre del servicio *</Label>
            <Input
              id="name"
              placeholder="Ej: Netflix, Disney+, HBO Max"
              value={formData.name}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, name: e.target.value }))
              }
            />
          </div>

          {/* Selector de Icono */}
          <div className="space-y-2">
            <Label>Icono del servicio *</Label>
            <div className="grid grid-cols-8 gap-2">
              {PREDEFINED_ICONS.map(({ icon, label }) => (
                <button
                  key={icon}
                  type="button"
                  title={label}
                  onClick={() => setFormData((prev) => ({ ...prev, icon }))}
                  className={cn(
                    "w-10 h-10 rounded-lg border-2 flex items-center justify-center text-xl transition-all hover:scale-110",
                    formData.icon === icon
                      ? "border-primary bg-primary/10 ring-2 ring-primary ring-offset-2"
                      : "border-border hover:border-muted-foreground"
                  )}
                >
                  {icon}
                </button>
              ))}
            </div>
            {formData.icon && (
              <p className="text-sm text-muted-foreground">
                Icono seleccionado: {formData.icon}
              </p>
            )}
          </div>

          {/* Selector de Color */}
          <div className="space-y-2">
            <Label>Color del servicio *</Label>
            <div className="grid grid-cols-8 gap-2">
              {PREDEFINED_COLORS.map(({ color, label }) => (
                <button
                  key={color}
                  type="button"
                  title={label}
                  onClick={() => setFormData((prev) => ({ ...prev, color }))}
                  className={cn(
                    "w-10 h-10 rounded-lg border-2 flex items-center justify-center transition-all hover:scale-110",
                    formData.color === color
                      ? "ring-2 ring-primary ring-offset-2"
                      : "border-transparent"
                  )}
                  style={{ backgroundColor: color }}
                >
                  {formData.color === color && (
                    <Check className="h-5 w-5 text-white drop-shadow-md" />
                  )}
                </button>
              ))}
            </div>

            {/* Color personalizado */}
            <div className="flex items-center gap-3 mt-3">
              <Label htmlFor="customColor" className="text-sm whitespace-nowrap">
                Color personalizado:
              </Label>
              <div className="flex items-center gap-2 flex-1">
                <input
                  type="color"
                  id="customColor"
                  value={formData.color || "#000000"}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, color: e.target.value }))
                  }
                  className="w-10 h-10 rounded cursor-pointer border-0"
                />
                <Input
                  placeholder="#000000"
                  value={formData.color}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, color: e.target.value }))
                  }
                  className="flex-1"
                />
              </div>
            </div>
          </div>

          {/* Preview */}
          {formData.name && formData.icon && formData.color && (
            <div className="space-y-2">
              <Label>Vista previa</Label>
              <div className="flex items-center gap-3 p-4 rounded-lg border bg-card">
                <div
                  className="w-12 h-12 rounded-xl flex items-center justify-center text-2xl"
                  style={{ backgroundColor: `${formData.color}20` }}
                >
                  {formData.icon}
                </div>
                <div>
                  <p className="font-semibold" style={{ color: formData.color }}>
                    {formData.name}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Estado */}
          <div className="space-y-2">
            <Label htmlFor="status">Estado</Label>
            <Select
              value={formData.status}
              onValueChange={(value) =>
                setFormData((prev) => ({
                  ...prev,
                  status: value as EstreamingServiceStatus,
                }))
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="Selecciona el estado" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={EstreamingServiceStatus.ACTIVE}>Activo</SelectItem>
                <SelectItem value={EstreamingServiceStatus.INACTIVE}>Inactivo</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleClose}>
              Cancelar
            </Button>
            <Button type="submit" disabled={updateMutation.isPending}>
              {updateMutation.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Guardar
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
